import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { changeSidebarVisibility } from "../slices/thunks";
import ResponsiveSearch from "Components/CommonComponent/ResponsiveSearch/ResponsiveSearch";
import ProfileDropdown from "../Components/Common/ProfileDropdown";
import { useLiveEnvironment } from "Components/Hooks/useLiveEnvironment";

/* ============================================================
   REAL ESTATE MOTIVATIONAL SLANGS — Day-wise × Phase-wise
   ============================================================ */
const REAL_ESTATE_SLANGS = {
  sunrise: [
    "First calls of the day close the best deals!",
    "Early birds lock the premium plots. Dial now!",
    "Golden hour for golden properties — start strong!",
    "Beat the competition — prospect before 9 AM!",
    "New day, new inventory, new closings. Let's go!",
    "Weekend buyers are scrolling right now. Call them!",
    "Sunday site visits start with Sunday morning calls!",
  ],
  morning: [
    "Hot leads go cold fast — follow up NOW!",
    "Your next crore booking is one call away!",
    "Pipeline full = wallet full. Keep pushing!",
    "Today's site visit is tomorrow's booking. Book it!",
    "Buyers are serious in the morning. Catch them!",
    "Saturday energy = double the deals. Sprint hard!",
    "Fresh week, fresh targets. Let's dominate the floor!",
  ],
  noon: [
    "Follow-up sprint active — zero leads left unattended!",
    "Lunch break for others, deal time for you!",
    "Midday push: convert those pending site visits!",
    "Hot weather = hot deals. Strike while it's hot!",
    "Your CRM is your goldmine — mine it right now!",
    "Afternoon momentum: every call can flip the month!",
    "Closings happen in the afternoon. Be relentless!",
  ],
  evening: [
    "Evening walkthroughs convert better — close strong!",
    "Final hours = final push. One more closure today!",
    "Golden sunset, golden opportunity — seal the deal!",
    "Review your pipeline before the day dies. Act!",
    "Evening calls hit differently. Dial that hot lead!",
    "Today's last effort is tomorrow's first success!",
    "End the week like a champion — close something!",
  ],
  night: [
    "Review today's leads. Tomorrow starts tonight!",
    "Top closers plan at night, dominate in the morning.",
    "Update your CRM. Yesterday's notes are today's deals.",
    "Night owls find the hidden gems in the inbox!",
    "Prep your pitch deck for tomorrow's big meeting.",
    "The best agents never stop — strategy never sleeps!",
    "One last follow-up text before midnight. Do it!",
  ],
};

/* ============================================================
   CLOCK THEME — Phase-wise colors
   ============================================================ */
const CLOCK_THEME = {
  sunrise: { dot: "#fb923c", digits: "#7c2d12", ampm: "#ea580c" },
  morning: { dot: "#3b82f6", digits: "#1e3a5f", ampm: "#3b82f6" },
  noon: { dot: "#f59e0b", digits: "#78350f", ampm: "#b45309" },
  evening: { dot: "#a855f7", digits: "#4c1d95", ampm: "#7c3aed" },
  night: { dot: "#818cf8", digits: "#e0e7ff", ampm: "#a5b4fc" },
};

/* ============================================================
   LIVE CLOCK COMPONENT
   ============================================================ */
const LiveClock = ({ phase }) => {
  const [time, setTime] = useState(() => {
    const now = new Date();
    return {
      h: now.getHours(),
      m: now.getMinutes(),
      date: now,
    };
  });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime({
        h: now.getHours(),
        m: now.getMinutes(),
        date: now,
      });
    };

    const msToNext = 60000 - (Date.now() % 60000);
    let interval;

    const timeout = setTimeout(() => {
      tick();
      interval = setInterval(tick, 60000);
    }, msToNext);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  const theme = CLOCK_THEME[phase] || CLOCK_THEME.morning;

  const isPM = time.h >= 12;
  const h12 = time.h % 12 || 12;

  const digits = `${String(h12).padStart(2, "0")}:${String(time.m).padStart(2, "0")}`;

  const formattedDate = time.date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-"); // 15-Jun-2026

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      {/* Pulsing dot */}
      <span
        style={{
          display: "inline-block",
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          background: theme.dot,
          flexShrink: 0,
          animation: "clockDotPulse 1.2s ease-in-out infinite",
        }}
      />

      {/* Date + Time */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          lineHeight: 1.1,
        }}
      >
        {/* Date */}
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: theme.ampm,
            opacity: 0.8,
          }}
        >
          {formattedDate}
        </span>

        {/* Time */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1px",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: 800,
              letterSpacing: "1.2px",
              fontVariantNumeric: "tabular-nums",
              color: theme.digits,
            }}
          >
            {digits}
          </span>

          <span
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: theme.ampm,
            }}
          >
            {isPM ? "PM" : "AM"}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   ENHANCED SVG COMPONENTS — Real Estate Themed
   ============================================================ */

const SunriseSVG = () => (
  <svg
    className="premium-weather-svg live-pulse"
    viewBox="0 0 64 64"
    width="32"
    height="32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="srAura" cx="50%" cy="60%" r="50%">
        <stop offset="0%" stopColor="#ff7e5f" stopOpacity="0.7" />
        <stop offset="100%" stopColor="#feb47b" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="srCore" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#fff4e0" />
        <stop offset="100%" stopColor="#ff7e5f" />
      </radialGradient>
    </defs>
    <ellipse cx="32" cy="46" rx="26" ry="10" fill="url(#srAura)" />
    <line x1="6" y1="46" x2="58" y2="46" stroke="#feb47b" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M18 46a14 14 0 0128 0z" fill="url(#srCore)" />
    <g stroke="#ff7e5f" strokeWidth="2.5" strokeLinecap="round" opacity="0.9">
      <line x1="32" y1="8" x2="32" y2="16" />
      <line x1="18" y1="14" x2="21.5" y2="21" />
      <line x1="46" y1="14" x2="42.5" y2="21" />
      <line x1="10" y1="28" x2="18" y2="30" />
      <line x1="54" y1="28" x2="46" y2="30" />
    </g>
    <rect x="6" y="44" width="52" height="4" rx="2" fill="#feb47b" opacity="0.35" />
  </svg>
);

const MorningSVG = () => (
  <svg
    className="premium-weather-svg live-spin"
    viewBox="0 0 64 64"
    width="32"
    height="32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="mAura" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#1e90ff" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="mCore" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#3b82f6" />
      </radialGradient>
    </defs>
    <circle cx="32" cy="32" r="28" fill="url(#mAura)" />
    <g stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round">
      <line x1="32" y1="4" x2="32" y2="12" />
      <line x1="32" y1="52" x2="32" y2="60" />
      <line x1="4" y1="32" x2="12" y2="32" />
      <line x1="52" y1="32" x2="60" y2="32" />
      <line x1="12" y1="12" x2="18" y2="18" />
      <line x1="52" y1="12" x2="46" y2="18" />
      <line x1="12" y1="52" x2="18" y2="46" />
      <line x1="52" y1="52" x2="46" y2="46" />
    </g>
    <circle cx="32" cy="32" r="12" fill="url(#mCore)" />
    <circle cx="29" cy="29" r="4" fill="white" opacity="0.4" />
  </svg>
);

const NoonSVG = ({ isHeat }) => (
  <svg
    className="premium-weather-svg live-spin"
    viewBox="0 0 64 64"
    width="34"
    height="34"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="nAura" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={isHeat ? "#ff5500" : "#fbbf24"} stopOpacity="0.7" />
        <stop offset="60%" stopColor={isHeat ? "#ff8800" : "#f59e0b"} stopOpacity="0.35" />
        <stop offset="100%" stopColor="#ffcc00" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="nCore" cx="40%" cy="38%" r="55%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor={isHeat ? "#ff5500" : "#f59e0b"} />
      </radialGradient>
    </defs>
    {isHeat && (
      <>
        <circle cx="32" cy="32" r="30" fill="none" stroke="#ff5500" strokeWidth="1" opacity="0.2" />
        <circle cx="32" cy="32" r="26" fill="none" stroke="#ff7700" strokeWidth="1" opacity="0.2" />
      </>
    )}
    <circle cx="32" cy="32" r="28" fill="url(#nAura)" />
    <g stroke={isHeat ? "#ff5500" : "#fbbf24"} strokeWidth="3" strokeLinecap="round">
      <line x1="32" y1="2" x2="32" y2="12" />
      <line x1="32" y1="52" x2="32" y2="62" />
      <line x1="2" y1="32" x2="12" y2="32" />
      <line x1="52" y1="32" x2="62" y2="32" />
      <line x1="10" y1="10" x2="17" y2="17" />
      <line x1="54" y1="10" x2="47" y2="17" />
      <line x1="10" y1="54" x2="17" y2="47" />
      <line x1="54" y1="54" x2="47" y2="47" />
    </g>
    <circle cx="32" cy="32" r="13" fill="url(#nCore)" />
    <circle cx="28" cy="28" r="4" fill="white" opacity="0.35" />
  </svg>
);

const EveningSVG = () => (
  <svg
    className="premium-weather-svg live-pulse"
    viewBox="0 0 64 64"
    width="32"
    height="32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="evGlow" cx="50%" cy="80%" r="65%">
        <stop offset="0%" stopColor="#f97316" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#ef4444" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="evSky" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0.6" />
      </linearGradient>
      <radialGradient id="evSun" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#fff0e0" />
        <stop offset="100%" stopColor="#ef4444" />
      </radialGradient>
    </defs>
    <rect x="4" y="8" width="56" height="44" rx="4" fill="url(#evSky)" />
    <ellipse cx="32" cy="48" rx="28" ry="10" fill="url(#evGlow)" />
    <path d="M16 46a16 16 0 0132 0z" fill="url(#evSun)" />
    <line x1="4" y1="46" x2="60" y2="46" stroke="#f97316" strokeWidth="2" strokeLinecap="round" />
    <g stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" opacity="0.8">
      <line x1="32" y1="14" x2="32" y2="22" />
      <line x1="16" y1="22" x2="20" y2="28" />
      <line x1="48" y1="22" x2="44" y2="28" />
      <line x1="8" y1="36" x2="16" y2="37" />
      <line x1="56" y1="36" x2="48" y2="37" />
    </g>
    <rect x="6" y="38" width="6" height="8" fill="#1e1b4b" opacity="0.6" rx="1" />
    <rect x="14" y="34" width="5" height="12" fill="#1e1b4b" opacity="0.6" rx="1" />
    <rect x="46" y="36" width="5" height="10" fill="#1e1b4b" opacity="0.6" rx="1" />
    <rect x="52" y="33" width="6" height="13" fill="#1e1b4b" opacity="0.6" rx="1" />
    <rect x="8" y="40" width="2" height="2" fill="#fbbf24" opacity="0.9" rx="0.5" />
    <rect x="16" y="36" width="2" height="2" fill="#fbbf24" opacity="0.9" rx="0.5" />
    <rect x="48" y="38" width="2" height="2" fill="#fbbf24" opacity="0.9" rx="0.5" />
    <rect x="54" y="35" width="2" height="2" fill="#fbbf24" opacity="0.9" rx="0.5" />
  </svg>
);

const NightSVG = () => (
  <svg
    className="premium-weather-svg live-sway"
    viewBox="0 0 64 64"
    width="32"
    height="32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="nightBg" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#1e1b4b" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#0f0c2b" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="moonGlow" cx="40%" cy="40%" r="55%">
        <stop offset="0%" stopColor="#e0e7ff" />
        <stop offset="100%" stopColor="#a5b4fc" />
      </radialGradient>
    </defs>
    <circle cx="32" cy="32" r="28" fill="url(#nightBg)" />
    <circle cx="32" cy="30" r="14" fill="url(#moonGlow)" />
    <circle cx="38" cy="26" r="11" fill="#1e1b4b" />
    <circle cx="14" cy="12" r="1.5" fill="#e0e7ff" opacity="0.9" />
    <circle cx="50" cy="10" r="1" fill="#c7d2fe" opacity="0.9" />
    <circle cx="54" cy="24" r="1.5" fill="#e0e7ff" opacity="0.8" />
    <circle cx="8" cy="30" r="1" fill="#c7d2fe" opacity="0.8" />
    <circle cx="20" cy="50" r="1.5" fill="#e0e7ff" opacity="0.7" />
    <circle cx="46" cy="52" r="1" fill="#c7d2fe" opacity="0.7" />
    <ellipse
      cx="26"
      cy="24"
      rx="4"
      ry="3"
      fill="white"
      opacity="0.2"
      transform="rotate(-20 26 24)"
    />
    <rect x="4" y="46" width="7" height="14" fill="#0f0c2b" opacity="0.7" rx="1" />
    <rect x="12" y="40" width="5" height="20" fill="#0f0c2b" opacity="0.7" rx="1" />
    <rect x="18" y="44" width="6" height="16" fill="#0f0c2b" opacity="0.7" rx="1" />
    <rect x="40" y="42" width="6" height="18" fill="#0f0c2b" opacity="0.7" rx="1" />
    <rect x="47" y="38" width="5" height="22" fill="#0f0c2b" opacity="0.7" rx="1" />
    <rect x="53" y="44" width="7" height="16" fill="#0f0c2b" opacity="0.7" rx="1" />
    <rect x="6" y="48" width="2" height="2" fill="#fbbf24" opacity="0.8" rx="0.5" />
    <rect x="13" y="42" width="2" height="2" fill="#60a5fa" opacity="0.8" rx="0.5" />
    <rect x="14" y="47" width="2" height="2" fill="#fbbf24" opacity="0.7" rx="0.5" />
    <rect x="19" y="46" width="2" height="2" fill="#60a5fa" opacity="0.8" rx="0.5" />
    <rect x="42" y="44" width="2" height="2" fill="#fbbf24" opacity="0.8" rx="0.5" />
    <rect x="48" y="40" width="2" height="2" fill="#60a5fa" opacity="0.8" rx="0.5" />
    <rect x="49" y="46" width="2" height="2" fill="#fbbf24" opacity="0.7" rx="0.5" />
    <rect x="55" y="46" width="2" height="2" fill="#60a5fa" opacity="0.8" rx="0.5" />
  </svg>
);

/* ============================================================
   MAIN HEADER COMPONENT
   ============================================================ */
const Header = ({ headerClass }) => {
  const dispatch = useDispatch();
  const { currentTime, liveTemp, timePhase } = useLiveEnvironment();

  const greetingConfig = useMemo(() => {
    const dayOfWeek = currentTime.getDay();
    const isExtremeHeat = liveTemp >= 35;

    const slogans = REAL_ESTATE_SLANGS[timePhase] || REAL_ESTATE_SLANGS.morning;
    const shortMotive = slogans[dayOfWeek] || slogans[0];

    switch (timePhase) {
      case "sunrise":
        return {
          text: "Good Morning",
          subText: `Early hustle! ${shortMotive}`,
          bgClass: "header-bg-sunrise",
          textThemeClass: "text-theme-sunrise",
          userHighlightClass: "user-badge-sunrise",
          badgeStyle: {
            background: "rgba(255, 126, 95, 0.12)",
            border: "1px solid rgba(255, 126, 95, 0.3)",
            boxShadow: "0 0 18px rgba(255, 126, 95, 0.25)",
          },
          nameStyle: {
            background: "rgba(255, 126, 95, 0.12)",
            color: "#c2410c",
            border: "1px solid rgba(255, 126, 95, 0.4)",
            borderRadius: "5px",
            padding: "2px 10px",
            fontWeight: 700,
          },
          svg: <SunriseSVG />,
        };

      case "morning":
        return {
          text: "Good Morning",
          subText: `Fresh leads are waiting! ${shortMotive}`,
          bgClass: "header-bg-morning",
          textThemeClass: "text-theme-morning",
          userHighlightClass: "user-badge-morning",
          badgeStyle: {
            background: "rgba(59, 130, 246, 0.1)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            boxShadow: "0 0 18px rgba(59, 130, 246, 0.2)",
          },
          nameStyle: {
            background: "rgba(59, 130, 246, 0.1)",
            color: "#1d4ed8",
            border: "1px solid rgba(59, 130, 246, 0.35)",
            borderRadius: "5px",
            padding: "2px 10px",
            fontWeight: 700,
          },
          svg: <MorningSVG />,
        };

      case "noon":
        return {
          text: "Good Afternoon",
          subText: `Follow-up sprint active! ${shortMotive}`,
          bgClass: "header-bg-noon",
          textThemeClass: "text-theme-noon",
          userHighlightClass: "user-badge-noon",
          badgeStyle: {
            background: isExtremeHeat ? "rgba(239, 68, 68, 0.1)" : "rgba(245, 158, 11, 0.1)",
            border: `1px solid ${isExtremeHeat ? "rgba(239,68,68,0.35)" : "rgba(245,158,11,0.35)"}`,
            boxShadow: isExtremeHeat
              ? "0 0 22px rgba(239, 68, 68, 0.25)"
              : "0 0 18px rgba(245, 158, 11, 0.22)",
          },
          nameStyle: {
            background: isExtremeHeat ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)",
            color: isExtremeHeat ? "#b91c1c" : "#b45309",
            border: `1px solid ${isExtremeHeat ? "rgba(239,68,68,0.35)" : "rgba(245,158,11,0.35)"}`,
            borderRadius: "5px",
            padding: "2px 10px",
            fontWeight: 700,
          },
          svg: <NoonSVG isHeat={isExtremeHeat} />,
        };

      case "evening":
        return {
          text: "Good Evening",
          subText: `Final push time! ${shortMotive}`,
          bgClass: "header-bg-evening",
          textThemeClass: "text-theme-evening",
          userHighlightClass: "user-badge-evening",
          badgeStyle: {
            background: "rgba(249, 115, 22, 0.1)",
            border: "1px solid rgba(249, 115, 22, 0.35)",
            boxShadow: "0 0 22px rgba(249, 115, 22, 0.25)",
          },
          nameStyle: {
            background: "rgba(249, 115, 22, 0.1)",
            color: "#9a3412",
            border: "1px solid rgba(249, 115, 22, 0.4)",
            borderRadius: "5px",
            padding: "2px 10px",
            fontWeight: 700,
          },
          svg: <EveningSVG />,
        };

      case "night":
      default:
        return {
          text: "Good Evening",
          subText: `Wrap up smart! ${shortMotive}`,
          bgClass: "header-bg-night",
          textThemeClass: "text-theme-night",
          userHighlightClass: "user-badge-night",
          badgeStyle: {
            background: "rgba(99, 102, 241, 0.1)",
            border: "1px solid rgba(99, 102, 241, 0.3)",
            boxShadow: "0 0 18px rgba(79, 70, 229, 0.25)",
          },
          nameStyle: {
            background: "rgba(99, 102, 241, 0.12)",
            color: "#4338ca",
            border: "1px solid rgba(99, 102, 241, 0.35)",
            borderRadius: "5px",
            padding: "2px 10px",
            fontWeight: 700,
          },
          svg: <NightSVG />,
        };
    }
  }, [currentTime, liveTemp, timePhase]);

  /* ── REDUX ── */
  const selectDashboardData = useMemo(
    () =>
      createSelector(
        state => state.Layout,
        state => ({ sidebarVisibilitytype: state.sidebarVisibilitytype }),
      ),
    [],
  );
  const { sidebarVisibilitytype } = useSelector(selectDashboardData);

  const profiledropdownData = useMemo(
    () =>
      createSelector(
        state => state.Profile,
        state => ({ user: state.user }),
      ),
    [],
  );
  const { user } = useSelector(profiledropdownData);

  const [userName, setUserName] = useState(() =>
    user?.firstName || user?.lastName
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : "Admin",
  );

  useEffect(() => {
    if (user?.firstName || user?.lastName)
      setUserName(`${user.firstName || ""} ${user.lastName || ""}`.trim());
  }, [user]);

  /* ── HAMBURGER TOGGLE ── */
  const toogleMenuBtn = useCallback(() => {
    const windowSize = document.documentElement.clientWidth;
    dispatch(changeSidebarVisibility("show"));

    const hamburgerIcon = document.querySelector(".hamburger-icon");
    if (windowSize > 767 && hamburgerIcon) hamburgerIcon.classList.toggle("open");

    const dataLayout = document.documentElement.getAttribute("data-layout");
    if (dataLayout === "horizontal") document.body.classList.toggle("menu");

    if (
      sidebarVisibilitytype === "show" &&
      (dataLayout === "vertical" || dataLayout === "semibox")
    ) {
      const sidebarSize = document.documentElement.getAttribute("data-sidebar-size");
      if (windowSize < 1025 && windowSize > 767) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.setAttribute(
          "data-sidebar-size",
          sidebarSize === "sm" ? "" : "sm",
        );
      } else if (windowSize > 1025) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.setAttribute(
          "data-sidebar-size",
          sidebarSize === "lg" ? "sm" : "lg",
        );
      } else if (windowSize <= 767) {
        document.body.classList.add("vertical-sidebar-enable");
        document.documentElement.setAttribute("data-sidebar-size", "lg");
      }
    }

    if (dataLayout === "twocolumn") document.body.classList.toggle("twocolumn-panel");
  }, [dispatch, sidebarVisibilitytype]);

  /* ── RENDER ── */
  return (
    <React.Fragment>
      {/* Clock dot pulse keyframe — ek baar inject */}
      <style>{`
        @keyframes clockDotPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(0.65); }
        }
      `}</style>

      <header
        id="page-topbar"
        className={`modern-premium-glass-header ${headerClass || ""} ${greetingConfig.bgClass}`}
      >
        <div className="layout-width">
          <div className="navbar-header">
            {/* ── LEFT: Hamburger + Greeting ── */}
            <div className="d-flex align-items-center gap-3 flex-grow-1 flex-md-grow-0">
              <button
                onClick={toogleMenuBtn}
                type="button"
                className="btn btn-sm px-2 fs-16 header-item vertical-menu-btn topnav-hamburger shadow-none"
                id="topnav-hamburger-icon"
              >
                <span className="hamburger-icon">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>

              {/* Greeting block — hidden on mobile */}
              <div className="enterprise-greetings-container d-none d-md-flex align-items-center gap-3">
                {/* SVG Icon + Clock capsule */}
                <div
                  className="premium-weather-badge-shell d-flex align-items-center gap-2"
                  style={greetingConfig.badgeStyle}
                >
                  {/* SVG icon — wahi purana */}
                  <div className="weather-svg-container d-flex align-items-center justify-content-center">
                    {greetingConfig.svg}
                  </div>

                  {/* TEMPERATURE — commented out, future use ke liye preserved
                  <span className="weather-temp-value fw-bold fs-14">{greetingConfig.temp}</span>
                  */}

                  {/* Live animated clock — temperature ki jagah */}
                  <LiveClock phase={timePhase} />
                </div>

                {/* Greeting text */}
                <div className={`text-greetings-lines ${greetingConfig.textThemeClass}`}>
                  <h5 className="m-0 greeting-title fw-bold fs-15 d-flex align-items-center gap-2 flex-wrap">
                    {greetingConfig.text},{" "}
                    <span
                      className={`colored-user-highlight ${greetingConfig.userHighlightClass}`}
                      style={greetingConfig.nameStyle}
                    >
                      {userName}
                    </span>
                  </h5>
                  <p className="m-0 greeting-subtitle fw-semibold opacity-75 fs-12 mt-1">
                    {greetingConfig.subText}
                  </p>
                </div>
              </div>
            </div>

            {/* ── RIGHT: Search + Profile ── */}
            <div className="d-flex align-items-center gap-3">
              <ResponsiveSearch />
              <ProfileDropdown />
            </div>
          </div>
        </div>
      </header>
    </React.Fragment>
  );
};

export default React.memo(Header);
